import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Plus,
  Edit,
  Trash2,
  Copy,
  Eye,
  Save,
  X,
  ArrowRight,
  CheckCircle2,
  Users,
  UserCircle,
  Settings,
  Clock,
  AlertCircle,
  FileText,
  ChevronRight,
  Workflow
} from 'lucide-react';
import {
  fetchAllTemplates,
  fetchTemplate,
  createTemplate,
  deleteTemplate
} from '../../../services/workflowTemplateService.ts';
import { fetchApprovers } from '../../../services/approverService.ts';
import { useNotify } from 'react-admin';

interface ApprovalStep {
  id: string;
  stepNo: number;
  Name: string;
  stepType: 'single' | 'parallel' | 'conditional';
  approvers: Approver[];
  approvalRule: 'all' | 'any' | 'majority';
  timeLimit?: number;
  description: string;
  isOptional: boolean;
}

interface Approver {
  id: string;
  name: string;
  role: string;
  email: string;
}

interface ApprovalTemplate {
  id?: string;
  Name: string;
  docType: string;
  description: string;
  steps: ApprovalStep[];
  isActive: boolean;
  createdDate: string;
  createdBy: number;
  lastModified: string;
  usageCount: number;
  stepsLoaded?: boolean;
}

type ApproverLike = Approver | string | number | { id?: string | number; name?: string; role?: string; email?: string };

const buildApproverMap = (pool: Approver[]) => new Map(pool.map(approver => [approver.id, approver]));

const normalizeApprover = (item: ApproverLike, poolMap: Map<string, Approver>): Approver => {
  if (item && typeof item === 'object') {
    const raw = item as { id?: string | number; name?: string; role?: string; email?: string };
    const id = raw.id != null ? String(raw.id) : '';
    const fromPool = id ? poolMap.get(id) : undefined;
    if (fromPool) {
      return {
        ...fromPool,
        name: raw.name || fromPool.name,
        role: raw.role || fromPool.role,
        email: raw.email || fromPool.email
      };
    }
    return {
      id,
      name: raw.name || id || '',
      role: raw.role || '-',
      email: raw.email || ''
    };
  }

  const id = item != null ? String(item) : '';
  const fromPool = id ? poolMap.get(id) : undefined;
  return fromPool || { id, name: id, role: '-', email: '' };
};

const normalizeApproverList = (items: ApproverLike[] | undefined, poolMap: Map<string, Approver>): Approver[] => {
  if (!items || items.length === 0) return [];
  return items.map(item => normalizeApprover(item, poolMap));
};

const needsApproverHydration = (items: ApproverLike[] | undefined): boolean => {
  if (!items || items.length === 0) return false;
  return items.some(item => {
    if (!item) return true;
    if (typeof item !== 'object') return true;
    const raw = item as { name?: string };
    return !raw.name;
  });
};

const TemplateList: React.FC = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  // const templateUrl = 'QmsWorkflowTemplate';

  const [templates, setTemplates] = useState<ApprovalTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<ApprovalTemplate | null>(null);
  const [isDesignerOpen, setIsDesignerOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [detailsLoadingId, setDetailsLoadingId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDocType, setFilterDocType] = useState('all');
  const [showApproverModal, setShowApproverModal] = useState(false);
  const [currentStepId, setCurrentStepId] = useState<string | null>(null);
  const [approversPool, setApproversPool] = useState<Approver[]>([]);
  const [isLoadingApprovers, setIsLoadingApprovers] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{ isOpen: boolean; action: (() => void) | null; message: string }>({
    isOpen: false,
    action: null,
    message: ''
  });

  const approverMap = useMemo(() => buildApproverMap(approversPool), [approversPool]);

  const getCreatedByLabel = (createdBy: number) => {
    const fromPool = approverMap.get(String(createdBy));
    return fromPool?.name || String(createdBy ?? '');
  };

  // Load templates with steps on component mount
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setIsLoading(true);
        const data = await fetchAllTemplates({ includeSteps: true });
        setTemplates(data.map(t => ({
          ...t,
          stepsLoaded: true,
          steps: (t.steps || []).map(s => ({
            ...s,
            id: s.id || `s${Math.random().toString(36).substr(2, 9)}`
          }))
        })));
      } catch (error) {
        console.error('Failed to load templates:', error);
        notify('Failed to load workflow templates', { type: 'error' });
      } finally {
        setIsLoading(false);
      }
    };

    loadTemplates();
  }, [notify]);

  useEffect(() => {
    if (approversPool.length > 0) return;

    const loadApprovers = async () => {
      try {
        setIsLoadingApprovers(true);
        const data = await fetchApprovers();
        setApproversPool(data);
      } catch (error) {
        console.error('Failed to load approvers:', error);
        notify('Failed to load approvers', { type: 'error' });
      } finally {
        setIsLoadingApprovers(false);
      }
    };

    loadApprovers();
  }, [approversPool.length, notify]);

  // Load approvers on-demand when modal or view opens
  useEffect(() => {
    if (!showApproverModal && !isDesignerOpen) return;
    if (approversPool.length > 0) return;

    const loadApprovers = async () => {
      try {
        setIsLoadingApprovers(true);
        const data = await fetchApprovers();
        setApproversPool(data);
      } catch (error) {
        console.error('Failed to load approvers:', error);
        notify('Failed to load approvers', { type: 'error' });
      } finally {
        setIsLoadingApprovers(false);
      }
    };

    loadApprovers();
  }, [showApproverModal, isDesignerOpen, approversPool.length, notify]);

  useEffect(() => {
    if (approversPool.length === 0) return;

    const poolMap = buildApproverMap(approversPool);
    setTemplates(prev => {
      let anyChanged = false;
      const updated = prev.map(template => {
        if (!template.steps || template.steps.length === 0) return template;
        let stepsChanged = false;
        const updatedSteps = template.steps.map(step => {
          const rawApprovers = step.approvers as unknown as ApproverLike[];
          if (!needsApproverHydration(rawApprovers)) {
            return step;
          }
          stepsChanged = true;
          return {
            ...step,
            approvers: normalizeApproverList(rawApprovers, poolMap)
          };
        });
        if (!stepsChanged) return template;
        anyChanged = true;
        return { ...template, steps: updatedSteps };
      });
      return anyChanged ? updated : prev;
    });
  }, [approversPool]);

  useEffect(() => {
    if (!selectedTemplate || approversPool.length === 0) return;

    const poolMap = buildApproverMap(approversPool);
    let changed = false;
    const updatedSteps = selectedTemplate.steps.map(step => {
      const rawApprovers = step.approvers as unknown as ApproverLike[];
      if (!needsApproverHydration(rawApprovers)) {
        return step;
      }
      changed = true;
      return {
        ...step,
        approvers: normalizeApproverList(rawApprovers, poolMap)
      };
    });

    if (changed) {
      setSelectedTemplate({ ...selectedTemplate, steps: updatedSteps });
    }
  }, [selectedTemplate, approversPool]);

  // Filtered templates
  const filteredTemplates = useMemo(() => {
    return templates.filter(template => {
      const matchesSearch = (template.Name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (template.description || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesDocType = filterDocType === 'all' || template.docType === filterDocType;
      return matchesSearch && matchesDocType;
    });
  }, [templates, searchTerm, filterDocType]);

  const ensureTemplateDetails = async (template: ApprovalTemplate) => {
    if (template.stepsLoaded) {
      return template;
    }

    if (!template.id) {
      throw new Error('Template ID is missing');
    }

    setDetailsLoadingId(template.id);
    try {
      const fullTemplate = await fetchTemplate(template.id);
      const mergedTemplate: ApprovalTemplate = {
        ...fullTemplate,
        stepsLoaded: true,
        steps: (fullTemplate.steps || []).map(s => ({
          ...s,
          id: s.id || `s${Math.random().toString(36).substr(2, 9)}`
        }))
      };
      setTemplates(prev => prev.map(t => (t.id === template.id ? mergedTemplate : t)));
      return mergedTemplate;
    } finally {
      setDetailsLoadingId(null);
    }
  };

  // Handle view template
  const handleViewTemplate = async (template: ApprovalTemplate) => {
    setSelectedTemplate(JSON.parse(JSON.stringify(template)));
    setIsEditing(false);
    setIsDesignerOpen(true);

    try {
      const fullTemplate = await ensureTemplateDetails(template);
      setSelectedTemplate(JSON.parse(JSON.stringify(fullTemplate)));
    } catch (error) {
      console.error('Failed to load template details:', error);
      notify('Failed to load workflow details', { type: 'error' });
    }
  };

  // Handle edit template
  const handleEditTemplate = (template: ApprovalTemplate) => {
    navigate(`/QmsWorkflowTemplate/${template.id}`);
  };

  // Handle create new template
  const handleCreateTemplate = () => {
    navigate(`/QmsWorkflowTemplate/create`);
  };

  // Handle delete template
  const handleDeleteTemplate = (id: string) => {
    setConfirmDialog({
      isOpen: true,
      message: 'Are you sure you want to delete this approval workflow template?',
      action: async () => {
        try {
          await deleteTemplate(id);
          setTemplates(templates.filter(t => t.id !== id));
          setConfirmDialog({ isOpen: false, action: null, message: '' });
          notify('Template deleted successfully', { type: 'success' });
        } catch (error) {
          console.error('Failed to delete template:', error);
          notify('Failed to delete template', { type: 'error' });
        }
      }
    });
  };

  // Handle duplicate template
  const handleDuplicateTemplate = async (template: ApprovalTemplate) => {
    try {
      const sourceTemplate = await ensureTemplateDetails(template);
      const newTemplate: any = {
        ...JSON.parse(JSON.stringify(sourceTemplate)),
        id: undefined,
        Name: `${sourceTemplate.Name} (Copy)`,
        createdDate: new Date().toISOString().split('T')[0],
        lastModified: new Date().toISOString().split('T')[0],
        usageCount: 0
      };
      
      const templateForApi = {
        ...newTemplate,
        // docType is already set correctly
      } as any;
      
      const created = await createTemplate(templateForApi);
      const newTemplateWithSteps: ApprovalTemplate = {
        ...created,
        stepsLoaded: true,
        steps: (created.steps || []).map(s => ({
          ...s,
          id: s.id || `s${Math.random().toString(36).substr(2, 9)}`
        }))
      };
      setTemplates([...templates, newTemplateWithSteps]);
      notify('Template copied successfully', { type: 'success' });
    } catch (error) {
      console.error('Failed to duplicate template:', error);
      notify('Failed to duplicate template', { type: 'error' });
    }
  };

  // Update template field
  const updateTemplateField = (field: keyof ApprovalTemplate, value: any) => {
    if (selectedTemplate) {
      setSelectedTemplate({ ...selectedTemplate, [field]: value });
    }
  };

  // Add new step
  const handleAddStep = () => {
    if (selectedTemplate) {
      const newStep: ApprovalStep = {
        id: `s${selectedTemplate.steps.length + 1}`,
        stepNo: selectedTemplate.steps.length + 1,
        Name: `Step ${selectedTemplate.steps.length + 1}`,
        stepType: 'single',
        approvers: [],
        approvalRule: 'all',
        timeLimit: 2,
        description: '',
        isOptional: false
      };
      setSelectedTemplate({
        ...selectedTemplate,
        steps: [...selectedTemplate.steps, newStep]
      });
    }
  };

  // Delete step
  const handleDeleteStep = (stepId: string) => {
    setConfirmDialog({
      isOpen: true,
      message: 'Are you sure you want to delete this step?',
      action: () => {
        if (selectedTemplate) {
          const updatedSteps = selectedTemplate.steps
            .filter(s => s.id !== stepId)
            .map((s, idx) => ({ ...s, stepNo: idx + 1 }));
          setSelectedTemplate({
            ...selectedTemplate,
            steps: updatedSteps
          });
        }
        setConfirmDialog({ isOpen: false, action: null, message: '' });
      }
    });
  };

  // Update step field
  const updateStepField = (stepId: string, field: keyof ApprovalStep, value: any) => {
    if (selectedTemplate) {
      setSelectedTemplate({
        ...selectedTemplate,
        steps: selectedTemplate.steps.map(s =>
          s.id === stepId ? { ...s, [field]: value } : s
        )
      });
    }
  };

  // Add approver to step
  const handleAddApprover = (stepId: string, approver: Approver) => {
    if (selectedTemplate) {
      setSelectedTemplate({
        ...selectedTemplate,
        steps: selectedTemplate.steps.map(s =>
          s.id === stepId ? { ...s, approvers: [...s.approvers, approver] } : s
        )
      });
    }
  };

  // Remove approver from step
  const handleRemoveApprover = (stepId: string, approverId: string) => {
    if (selectedTemplate) {
      setSelectedTemplate({
        ...selectedTemplate,
        steps: selectedTemplate.steps.map(s =>
          s.id === stepId ? { ...s, approvers: s.approvers.filter(a => a.id !== approverId) } : s
        )
      });
    }
  };

  // Save template (for view mode only now)
  const handleSaveTemplate = () => {
    // This function is now handled in TemplateCreate and TemplateEdit pages
    setIsDesignerOpen(false);
  };

  // Get approval rule label
  const getApprovalRuleLabel = (rule: string) => {
    const labels: Record<string, string> = {
      all: 'All Approve',
      any: 'Any Approve',
      // majority: 'Majority Approve'
    };
    return labels[rule] || rule;
  };

  // Get step type badge
  const getStepTypeBadge = (type: string) => {
    const configs: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
      single: {
        label: 'Single Approver',
        color: 'bg-blue-100 text-blue-800 border-blue-200',
        icon: <UserCircle className="size-3" />
      },
      parallel: {
        label: 'Parallel Approval',
        color: 'bg-purple-100 text-purple-800 border-purple-200',
        icon: <Users className="size-3" />
      },
      // conditional: {
      //   label: '条件审批',
      //   color: 'bg-orange-100 text-orange-800 border-orange-200',
      //   icon: <GitBranch className="size-3" />
      // }
    };
    const config = configs[type] || configs.single;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${config.color}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Approval Workflow Template Design</h1>
        <p className="text-gray-600">Create and manage document approval workflow templates</p>
      </div>

      {/* Filters and Actions */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-4 items-center">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search template name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="lg:w-48">
            <select
              value={filterDocType}
              onChange={(e) => setFilterDocType(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
            >
              <option value="all">All Document Types</option>
              <option value="SOP">SOP</option>
              {/* <option value="WI">Work Instruction</option>
              <option value="FORM">Form</option>
              <option value="POLICY">Policy</option> */}
            </select>
          </div>
          <button
            onClick={handleCreateTemplate}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
          >
            <Plus className="size-5" />
            Create New Template
          </button>
        </div>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {isLoading ? (
          <div className="col-span-full bg-white border border-gray-200 rounded-lg p-12 text-center">
            <div className="inline-block">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
            <p className="text-gray-600 mt-4">Loading templates...</p>
          </div>
        ) : filteredTemplates.length === 0 ? null : (
          filteredTemplates.map((template) => (
          <div key={template.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
            {/* Template Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-bold text-gray-900">{template.Name}</h3>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      template.isActive 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {template.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <FileText className="size-4" />
                      <span>{template.docType}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Workflow className="size-4" />
                      <span>{template.stepsLoaded ? `${template.steps.length} Steps` : 'Steps: --'}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <CheckCircle2 className="size-4" />
                      <span>Used {template.usageCount} times</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => handleViewTemplate(template)}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  <Eye className="size-4" />
                  View
                </button>
                <button
                  onClick={() => handleEditTemplate(template)}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  <Edit className="size-4" />
                  Edit
                </button>
                <button
                  onClick={() => handleDuplicateTemplate(template)}
                  className="inline-flex items-center justify-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  <Copy className="size-4" />
                </button>
                <button
                  onClick={() => template.id && handleDeleteTemplate(template.id)}
                  className="inline-flex items-center justify-center gap-2 px-3 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors text-sm"
                >
                  <Trash2 className="size-4" />
                </button>
              </div>
            </div>

            {/* Workflow Visualization */}
            <div className="p-6 bg-gray-50">
              <h4 className="text-sm font-medium text-gray-700 mb-4">Approval Workflow:</h4>
              {!template.stepsLoaded ? (
                <div className="text-sm text-gray-600">
                  Workflow details load on demand. Click View to load.
                </div>
              ) : (
                <div className="space-y-3">
                  {template.steps.map((step, index) => (
                    <div key={step.id}>
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                            {step.stepNo}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <h5 className="font-medium text-gray-900">{step.Name}</h5>
                              {getStepTypeBadge(step.stepType)}
                              {step.isOptional && (
                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                                  Optional
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mb-2">{step.description}</p>
                            <div className="flex flex-wrap items-center gap-3 text-sm">
                              <div className="flex items-center gap-1 text-gray-600">
                                <Users className="size-4" />
                                <span>{step.approvers.map(a => a.name).join(', ') || 'Not Assigned'}</span>
                              </div>
                              {step.timeLimit && (
                                <div className="flex items-center gap-1 text-gray-600">
                                  <Clock className="size-4" />
                                  <span>{step.timeLimit} days</span>
                                </div>
                              )}
                              <div className="flex items-center gap-1 text-gray-600">
                                <Settings className="size-4" />
                                <span>{getApprovalRuleLabel(step.approvalRule)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      {index < template.steps.length - 1 && (
                        <div className="flex justify-center py-2">
                          <ChevronRight className="size-5 text-gray-400 transform rotate-90" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Template Footer */}
            <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>Created {template.createdDate}</span>
                <span>Created by: {getCreatedByLabel(template.createdBy)}</span>
              </div>
            </div>
          </div>
        ))
        )}
      </div>

      {/* Empty State */}
      {!isLoading && filteredTemplates.length === 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
          <Workflow className="size-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Approval Templates Found</h3>
          <p className="text-gray-600 mb-6">Try adjusting search criteria or create a new approval workflow template</p>
          <button
            onClick={handleCreateTemplate}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="size-5" />
            Create New Template
          </button>
        </div>
      )}

      {/* Template Designer Modal */}
      {isDesignerOpen && selectedTemplate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">
                  {isEditing ? 'Edit Approval Workflow Template' : 'View Approval Workflow Template'}
                </h2>
                <button
                  onClick={() => setIsDesignerOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="size-6" />
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Template Basic Info */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Template Name
                    </label>
                    <input
                      type="text"
                      value={selectedTemplate.Name}
                      readOnly={!isEditing}
                      onChange={(e) => updateTemplateField('Name', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 read-only:bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Document Type
                    </label>
                    <select
                      value={selectedTemplate.docType}
                      disabled={!isEditing}
                      onChange={(e) => updateTemplateField('docType', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 appearance-none"
                    >
                      <option value="SOP">SOP</option>
                      <option value="WI">Work Instruction</option>
                      <option value="FORM">Form</option>
                      <option value="POLICY">Policy</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={selectedTemplate.description}
                      readOnly={!isEditing}
                      onChange={(e) => updateTemplateField('description', e.target.value)}
                      rows={3}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 read-only:bg-gray-50"
                    />
                  </div>
                </div>
              </div>

              {/* Workflow Steps */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Approval Steps</h3>
                  {isEditing && (
                    <button 
                      onClick={handleAddStep}
                      className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    >
                      <Plus className="size-4" />
                      Add Step
                    </button>
                  )}
                </div>

                <div className="space-y-4">
                  {detailsLoadingId === selectedTemplate.id && !selectedTemplate.stepsLoaded ? (
                    <div className="text-center py-12 bg-gray-50 border border-dashed border-gray-300 rounded-lg">
                      <div className="inline-block">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                      </div>
                      <p className="text-gray-600 mt-3">Loading workflow details...</p>
                    </div>
                  ) : (
                    selectedTemplate.steps.map((step, index) => (
                      <div key={step.id}>
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                          <div className="flex items-start gap-4">
                            <div className="flex-shrink-0">
                              <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                              {step.stepNo}
                              </div>
                            </div>
                            <div className="flex-1">
                              <div className="grid grid-cols-2 gap-4 mb-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Step Name
                                  </label>
                                  <input
                                    type="text"
                                    value={step.Name}
                                    disabled={!isEditing}
                                    onChange={(e) => updateStepField(step.id || '', 'Name', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-white text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Step Type
                                  </label>
                                  <select
                                    value={step.stepType}
                                    disabled={!isEditing}
                                    onChange={(e) => updateStepField(step.id, 'stepType', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-white appearance-none text-sm"
                                  >
                                    <option value="single">Single Approver</option>
                                    <option value="parallel">Parallel Approval</option>
                                    <option value="conditional">Conditional Approval</option>
                                  </select>
                                </div>
                                <div className="col-span-2">
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Description
                                  </label>
                                  <textarea
                                    value={step.description}
                                    disabled={!isEditing}
                                    onChange={(e) => updateStepField(step.id, 'description', e.target.value)}
                                    rows={2}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-white text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Approval Rule
                                  </label>
                                  <select
                                    value={step.approvalRule}
                                    disabled={!isEditing}
                                    onChange={(e) => updateStepField(step.id, 'approvalRule', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-white appearance-none text-sm"
                                  >
                                    <option value="all">All Approvers</option>
                                    <option value="any">Any Approver</option>
                                    {/* <option value="majority">Majority</option> */}
                                  </select>
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Time Limit (days)
                                  </label>
                                  <input
                                    type="number"
                                    value={step.timeLimit || ''}
                                    disabled={!isEditing}
                                    onChange={(e) => updateStepField(step.id, 'timeLimit', parseInt(e.target.value) || 0)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-white text-sm"
                                  />
                                </div>
                              </div>

                              <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                  Approvers
                                </label>
                                <div className="flex flex-wrap gap-2">
                                  {step.approvers.map(approver => (
                                    <div
                                      key={approver.id}
                                      className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm"
                                    >
                                      <UserCircle className="size-4 text-gray-600" />
                                      <div>
                                        <div className="font-medium text-gray-900">{approver.name}</div>
                                        <div className="text-xs text-gray-500">{approver.role}</div>
                                      </div>
                                      {isEditing && (
                                        <button 
                                          onClick={() => handleRemoveApprover(step.id, approver.id)}
                                          className="ml-2 text-gray-400 hover:text-red-600"
                                        >
                                          <X className="size-4" />
                                        </button>
                                      )}
                                    </div>
                                  ))}
                                  {isEditing && (
                                    <button 
                                      onClick={() => {
                                        setCurrentStepId(step.id);
                                        setShowApproverModal(true);
                                      }}
                                      className="inline-flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                                    >
                                      <Plus className="size-4" />
                                      Add Approver
                                    </button>
                                  )}
                                </div>
                              </div>

                              <div className="flex items-center gap-4">
                                <label className="flex items-center gap-2 text-sm">
                                  <input
                                    type="checkbox"
                                    checked={step.isOptional}
                                    disabled={!isEditing}
                                    onChange={(e) => updateStepField(step.id, 'isOptional', e.target.checked)}
                                    className="size-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                  <span className="text-gray-700">This step is optional</span>
                                </label>
                                {isEditing && (
                                  <button 
                                    onClick={() => handleDeleteStep(step.id)}
                                    className="ml-auto inline-flex items-center gap-2 px-3 py-1.5 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors text-sm"
                                  >
                                    <Trash2 className="size-4" />
                                    Delete Step
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        {index < selectedTemplate.steps.length - 1 && (
                          <div className="flex justify-center py-3">
                            <ArrowRight className="size-6 text-gray-400 transform rotate-90" />
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>

                {selectedTemplate.steps.length === 0 && (
                  <div className="text-center py-12 bg-gray-50 border border-dashed border-gray-300 rounded-lg">
                    <Workflow className="size-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 mb-4">No approval steps yet</p>
                    {isEditing && (
                      <button 
                        onClick={handleAddStep}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Plus className="size-5" />
                        Add First Step
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setIsDesignerOpen(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {isEditing ? 'Cancel' : 'Close'}
                </button>
                {isEditing && (
                  <button
                    onClick={handleSaveTemplate}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Save className="size-5" />
                    Save Template
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approver Selection Modal */}
      {showApproverModal && currentStepId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Select Approver</h3>
                <button
                  onClick={() => {
                    setShowApproverModal(false);
                    setCurrentStepId(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="size-6" />
                </button>
              </div>
            </div>
            <div className="p-6">
              {isLoadingApprovers ? (
                <div className="text-center py-12 text-gray-600">
                  <div className="inline-block mb-3">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                  <p>Loading approvers...</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 max-h-[400px] overflow-y-auto">
                  {approversPool.map((approver: Approver) => {
                  const currentStep = selectedTemplate?.steps.find(s => s.id === currentStepId);
                  const isAlreadyAdded = currentStep?.approvers.some(a => a.id === approver.id);
                  
                  return (
                    <button
                      key={approver.id}
                      onClick={() => {
                        if (!isAlreadyAdded) {
                          handleAddApprover(currentStepId, approver);
                        }
                      }}
                      disabled={isAlreadyAdded}
                      className={`p-4 border rounded-lg text-left transition-colors ${
                        isAlreadyAdded
                          ? 'bg-gray-50 border-gray-200 cursor-not-allowed opacity-50'
                          : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <UserCircle className="size-10 text-gray-400" />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{approver.name}</div>
                          <div className="text-sm text-gray-600">{approver.role}</div>
                          <div className="text-xs text-gray-500">{approver.email}</div>
                        </div>
                        {isAlreadyAdded && (
                          <CheckCircle2 className="size-5 text-green-600" />
                        )}
                      </div>
                    </button>
                  );
                })}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowApproverModal(false);
                  setCurrentStepId(null);
                }}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {confirmDialog.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[70]">
          <div className="bg-white rounded-lg max-w-sm w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <AlertCircle className="size-6 text-red-600" />
                <h3 className="text-lg font-bold text-gray-900">Confirm Action</h3>
              </div>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-700">{confirmDialog.message}</p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setConfirmDialog({ isOpen: false, action: null, message: '' })}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => confirmDialog.action?.()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateList;
